import React, { useState, useEffect } from 'react';
import { 
  CurrencyDollarIcon,
  CogIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

const TaxSettings = () => {
  const [settings, setSettings] = useState({
    taxRate: 18,
    taxIncluded: true,
    freeShippingThreshold: 300,
    shippingCost: 25
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/settings/tax');
      setSettings(response.data.settings || settings);
    } catch (error) {
      console.error('Error fetching tax settings:', error);
      setMessage('שגיאה בטעינת הגדרות המס');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.post('/api/admin/settings/tax', settings);
      setMessage('ההגדרות נשמרו בהצלחה');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving tax settings:', error);
      setMessage('שגיאה בשמירת ההגדרות ');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateExample = () => {
    const basePrice = 100;
    const taxAmount = basePrice * (settings.taxRate / 100);
    const totalWithTax = settings.taxIncluded ? basePrice : basePrice + taxAmount;
    
    return {
      basePrice,
      taxAmount,
      totalWithTax,
      shipping: basePrice >= settings.freeShippingThreshold ? 0 : settings.shippingCost
    };
  };

  const example = calculateExample();

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="mr-2">טוען הגדרות...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6" dir="rtl">
      <div className="flex items-center mb-6">
        <CurrencyDollarIcon className="h-6 w-6 ml-2 text-blue-500" />
        <h2 className="text-xl font-semibold text-gray-900">הגדרות מס ומחירים</h2>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-md flex items-center ${
          message.includes('שגיאה') ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'
        }`}>
          {message.includes('שגיאה') ? (
            <ExclamationTriangleIcon className="h-5 w-5 ml-2" />
          ) : (
            <CheckCircleIcon className="h-5 w-5 ml-2" />
          )}
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Tax Settings */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <CogIcon className="h-5 w-5 ml-2" />
            הגדרות מס
          </h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              שיעור מע"מ (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={settings.taxRate}
              onChange={(e) => handleChange('taxRate', parseFloat(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.taxIncluded}
                onChange={(e) => handleChange('taxIncluded', e.target.checked)}
                className="ml-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm font-medium text-gray-700">
                המחירים כוללים מע"מ
              </span>
            </label>
            <p className="text-xs text-gray-500 mt-1">
              אם מסומן, המחירים המוצגים כוללים מע"מ. אם לא, המע"מ יתווסף בקופה.
            </p>
          </div>
        </div>

        {/* Shipping Settings */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <CogIcon className="h-5 w-5 ml-2" />
            הגדרות משלוח
          </h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              סף משלוח חינם (₪)
            </label>
            <input
              type="number"
              min="0"
              value={settings.freeShippingThreshold}
              onChange={(e) => handleChange('freeShippingThreshold', parseFloat(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              עלות משלוח (₪)
            </label>
            <input
              type="number"
              min="0"
              value={settings.shippingCost}
              onChange={(e) => handleChange('shippingCost', parseFloat(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Example Calculation */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="text-md font-medium text-gray-900 mb-3">דוגמה לחישוב:</h4>
        <div className="text-sm space-y-1">
          <div className="flex justify-between">
            <span>מחיר בסיס:</span>
            <span>₪{example.basePrice.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span>מע"מ ({settings.taxRate}%):</span>
            <span>₪{example.taxAmount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span>משלוח:</span>
            <span>{example.shipping === 0 ? 'חינם' : `₪${example.shipping.toFixed(2)}`}</span>
          </div>
          <div className="flex justify-between font-medium border-t pt-1">
            <span>סה"כ:</span>
            <span>₪{(example.totalWithTax + example.shipping).toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`px-6 py-2 rounded-md font-medium transition-colors ${
            saving
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {saving ? 'שומר...' : 'שמור הגדרות'}
        </button>
      </div>
    </div>
  );
};

export default TaxSettings;
